import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, useToast } from '@vd/ui';
import { api } from '../../lib/api';

export interface BranchNameModalProps {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  /** Project the ticket lives in, so we can invalidate the project's ticket list. */
  projectId: string;
  /** Current branch name; when empty/null/undefined the modal is in "Set"
   *  mode. Null vs undefined matches the wire shape (see
   *  TicketSummary.branchName). The `?? ''` coercion below handles both. */
  currentBranch?: string | null;
}

const BRANCH_MAX = 255;

export function BranchNameModal({ open, onClose, ticketId, projectId, currentBranch }: BranchNameModalProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(currentBranch ?? '');

  // Reset the input each time the modal opens so reopening on a different
  // ticket doesn't show the previous ticket's branch.
  useEffect(() => {
    if (open) setValue(currentBranch ?? '');
  }, [open, currentBranch]);

  const mutation = useMutation({
    mutationFn: (next: string | null) => api.updateTicket(ticketId, { branchName: next }),
    onSuccess: async () => {
      // Branch changes don't fire a live event server-side (the LiveEvent
      // ticket.updated payload is whitelisted to title/description/
      // titlePending), so nothing else will refresh these queries for us
      // — invalidate explicitly here.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['ticket', ticketId] }),
        qc.invalidateQueries({ queryKey: ['tickets', projectId] }),
        qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] }),
      ]);
    },
  });

  const trimmed = value.trim();
  const tooLong = trimmed.length > BRANCH_MAX;
  const unchanged = trimmed === (currentBranch ?? '').trim();
  const canSave = !tooLong && !unchanged && !mutation.isPending;

  const save = async () => {
    if (!canSave) return;
    try {
      await mutation.mutateAsync(trimmed === '' ? null : trimmed);
      toast.push({ variant: 'success', title: trimmed === '' ? 'Branch cleared' : 'Branch saved' });
      onClose();
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  const clear = async () => {
    try {
      await mutation.mutateAsync(null);
      toast.push({ variant: 'success', title: 'Branch cleared' });
      onClose();
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  const isEdit = !!currentBranch;

  return (
    <Modal open={open} onClose={onClose} width={460}>
      <ModalHeader>{isEdit ? 'Edit branch name' : 'Set branch name'}</ModalHeader>
      <ModalBody>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
          Records the git branch this ticket is being worked on. Free-form text: Toado doesn't validate it against a remote.
        </p>
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
          }}
          placeholder="e.g. fix-add-button-contrast"
        />
        {tooLong && (
          <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>
            Too long ({trimmed.length} / {BRANCH_MAX}).
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        {isEdit && (
          <Button variant="ghost" onClick={clear} disabled={mutation.isPending} style={{ marginRight: 'auto' }}>
            Clear
          </Button>
        )}
        <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!canSave}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
